import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, take, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import {
	TPayableInvoices,
	TPaymentQuote,
	TPaymentStatus,
	TQuoteChangedDetails,
	TTryToPayResult,
} from 'services/api/payments/types';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import { getErrorCode, getErrorDetails, getErrorMessage } from 'utils/apiError';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';
import { getTransactionsRequest } from '../transactions/reducer';
import {
	getPayableFailure,
	getPayableRequest,
	getPayableSuccess,
	getQuoteFailure,
	getQuoteRequest,
	getQuoteSuccess,
	tryToPayFailure,
	tryToPayProgress,
	tryToPayQuoteChanged,
	tryToPayQuoteExpired,
	tryToPayRequest,
	tryToPaySuccess,
} from './reducer';
import { TPayableRequest, TPaymentQuoteRequest, TTryToPayRequest } from './types';

// Outcome unknown ("processing"): poll GET api/payments/{transactionId}.
const STATUS_POLL_INTERVAL_MS = 3_000;
const STATUS_POLL_TIMEOUT_MS = 90_000;
const STILL_PROCESSING_MESSAGE =
	'The payment is being processed. You will see the result in Transaction history.';

function* ensureFreshToken() {
	yield put(refreshTokenByTimeOutRequest());
	yield take(refreshTokenByTimeOutSuccess.type);
}

const toNumber = (value: unknown): number | null =>
	typeof value === 'number' ? value : typeof value === 'string' && value !== '' ? +value : null;

const toResultStatus = (status: string): TTryToPayResult['status'] => {
	switch (status) {
		case 'succeeded':
			return 'succeeded';
		case 'pending':
			return 'pending';
		case 'failed':
		case 'cancelled':
			return 'failed';
		default:
			return 'processing';
	}
};

// ==========================================:

function* getPayableWorker(action: PayloadAction<TPayableRequest>) {
	try {
		yield call(ensureFreshToken);
		const response: TPayableInvoices = yield call(api.payments.getPayable);
		yield put(getPayableSuccess(response ?? { invoices: [] }));
	} catch (error) {
		yield put(getPayableFailure());
		if (!action.payload?.silent) notificationContainer(getErrorMessage(error), 'error');
	}
}

function* getQuoteWorker(action: PayloadAction<TPaymentQuoteRequest>) {
	try {
		yield call(ensureFreshToken);
		const response: TPaymentQuote = yield call(api.payments.getQuote, action.payload);
		yield put(getQuoteSuccess(response));
	} catch (error) {
		const message = getErrorMessage(error);
		yield put(getQuoteFailure(message));
		notificationContainer(message, 'error');
	}
}

function* pollPaymentStatus(result: TTryToPayResult) {
	let current = result;
	const startedAt = Date.now();
	while (current.status === 'processing' && Date.now() - startedAt < STATUS_POLL_TIMEOUT_MS) {
		yield delay(STATUS_POLL_INTERVAL_MS);
		try {
			yield call(ensureFreshToken);
			const status: TPaymentStatus = yield call(api.payments.getStatus, current.transactionId);
			const mapped = toResultStatus(status.status);
			current = {
				...current,
				status: mapped,
				isSuccess: mapped === 'succeeded' || mapped === 'pending',
				message: status.message || current.message,
				label: status.label ?? current.label ?? null,
				completedAt: status.completedAt ?? current.completedAt ?? null,
				receiptUrl: status.receiptUrl ?? current.receiptUrl ?? null,
				chargedAmount: status.chargedAmount ?? current.chargedAmount,
				chargedCurrency: status.chargedCurrency ?? current.chargedCurrency,
			};
		} catch {
			// a temporary error while polling: keep polling until the timeout
		}
	}
	if (current.status === 'processing') {
		current = { ...current, message: STILL_PROCESSING_MESSAGE };
	}
	return current;
}

function* tryToPayWorker(action: PayloadAction<TTryToPayRequest>) {
	const request = action.payload;
	try {
		yield call(ensureFreshToken);
		let result: TTryToPayResult = yield call(api.payments.tryToPay, request);

		if (result?.status === 'processing') {
			yield put(tryToPayProgress(result));
			result = yield call(pollPaymentStatus, result);
		}
		yield put(tryToPaySuccess(result));
	} catch (error) {
		const code = getErrorCode(error);
		if (code === 'quote_changed') {
			const details = getErrorDetails(error) ?? {};
			const changed: TQuoteChangedDetails = {
				amount: toNumber(details.amount) ?? 0,
				fee: toNumber(details.fee) ?? 0,
				total: toNumber(details.total) ?? 0,
			};
			yield put(tryToPayQuoteChanged(changed));
			if (request.quoteId) {
				// Direct Debit: the amounts are fixed by the quote, request a new one
				yield put(
					getQuoteRequest({
						invoiceId: request.invoiceId,
						paymentMethodId: request.paymentMethodId,
					}),
				);
			}
			return;
		}
		if (code === 'quote_expired') {
			yield put(tryToPayQuoteExpired());
			notificationContainer(getErrorMessage(error), 'info');
			yield put(
				getQuoteRequest({ invoiceId: request.invoiceId, paymentMethodId: request.paymentMethodId }),
			);
			return;
		}
		const message = getErrorMessage(error);
		yield put(tryToPayFailure(message));
		notificationContainer(message, 'error');
		return;
	}

	// the payment changed the invoice and the history
	yield put(getPayableRequest({ silent: true }));
	yield put(getTransactionsRequest({ pageNumber: 1, pageSize: PAGE_SIZE }));
}

export function* paymentsSaga() {
	yield takeLatest(getPayableRequest.type, getPayableWorker);
	yield takeLatest(getQuoteRequest.type, getQuoteWorker);
	yield takeLatest(tryToPayRequest.type, tryToPayWorker);
}
